import { isPlatformBrowser } from '@angular/common';
import {
	Component,
	DOCUMENT,
	EventEmitter,
	HostListener,
	Input,
	inject,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	PLATFORM_ID,
	Renderer2,
	SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import type { ErrorCode } from '@libs/errors/error-codes';
import { AuthService } from '@services/auth/auth.service';
import { CurrentUserService } from '@services/auth/current-user.service';
import { ViewportService } from '@services/geo/viewport.service';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { PhoneService } from '@services/shared/phone.service';
import { ValidationService } from '@services/ui/validation.service';
import { UserService } from '@services/user/user.service';
import { AvatarComponent } from '@shared/components/avatar/avatar.component';
import { ButtonComponent } from '@shared/components/button/button.component';
import { InputComponent } from '@shared/components/input/input.component';
import { PhoneComponent } from '@shared/components/phone/phone.component';
import { SharedModule } from '@shared/modules/shared.module';
import { translateApiError } from 'app/core/utils/api-error';
import { activeLocale } from 'app/core/utils/locale';
import { catchError, finalize, of } from 'rxjs';
import { ChangePasswordComponent } from './change-password/change-password.component';
import { MfaEnrollmentDialogComponent } from './mfa-enrollment-dialog/mfa-enrollment-dialog.component';
import { ProfileMfaSectionComponent } from './profile-mfa-section/profile-mfa-section.component';
import { ProfileMfaStateService } from './profile-mfa-section/profile-mfa-state.service';

interface UserProfile {
	email: string;
	firstName: string;
	lastName: string;
	secondLastName?: string | null;
	lastPasswordChange?: Date;
	phone?: string | null;
	phone2?: string | null;
	roles?: string[];
	createdAt?: string;
	lastLoginAt?: string | null;
}

@Component({
	selector: 'app-profile',
	imports: [
		SharedModule,
		AvatarComponent,
		ButtonComponent,
		ChangePasswordComponent,
		MfaEnrollmentDialogComponent,
		ReactiveFormsModule,
		InputComponent,
		PhoneComponent,
		ProfileMfaSectionComponent,
	],
	// One 2FA status for the desktop dialog and the mobile sheet sections
	providers: [ProfileMfaStateService],
	templateUrl: './profile.component.html',
	styleUrl: './profile.component.scss',
})
export class ProfileComponent implements OnInit, OnChanges, OnDestroy {
	@Input() visible = false;
	@Output() visibleChange = new EventEmitter<boolean>();

	user: UserProfile | null = null;
	showPasswordDialog = false;
	/** Desktop: inline password fields (app-change-password variant="inline") open in the security card */
	showPasswordFields = false;
	isLoading = true;
	profileForm: FormGroup;
	showSecondPhone = false;
	isEditMode = false;

	/** 2FA status, shared with the app-profile-mfa-section rows */
	readonly mfa = inject(ProfileMfaStateService);

	// Client detection
	isClient = false;

	get userId(): number | undefined {
		return this.currentUserService.getUserId();
	}

	private lastLoadTime: number = 0;
	private readonly cacheDuration = 5 * 60 * 1000;
	private originalFormValues: any = null;

	private readonly currentUserService = inject(CurrentUserService);
	private readonly authService = inject(AuthService);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly fb = inject(FormBuilder);
	private readonly messageService = inject(MessageAlertService);
	private readonly userService = inject(UserService);
	private readonly viewportService = inject(ViewportService);
	private readonly renderer = inject(Renderer2);
	private readonly document = inject<Document>(DOCUMENT);
	private readonly validationService = inject(ValidationService);
	private readonly translocoService = inject(TranslocoService);
	private readonly phoneService = inject(PhoneService);

	constructor() {
		this.profileForm = this.fb.group({
			firstName: ['', Validators.required],
			lastName: ['', Validators.required],
			secondLastName: [''],
			email: ['', [Validators.required, Validators.email]],
			phone: [''],
			phone2: [''],
			// Only asked (and sent) when the email changes
			currentPassword: [''],
		});
	}

	ngOnInit() {
		this.isClient = this.currentUserService.hasRole('client');
		// Profile data is loaded lazily via ngOnChanges when visible becomes true
	}

	addSecondPhone(): void {
		this.showSecondPhone = true;
	}

	removeSecondPhone(): void {
		this.showSecondPhone = false;
		this.profileForm.get('phone2')?.setValue('');
	}

	private invalidateCache(): void {
		this.lastLoadTime = 0;
	}

	loadUserProfile(): void {
		const userId = this.currentUserService.getUserId();

		if (!userId) {
			//console.warn('⚠️ [Profile] No userId found');
			this.isLoading = false;
			return;
		}

		const now = Date.now();
		const cacheValid = this.user && now - this.lastLoadTime < this.cacheDuration;

		if (cacheValid) {
			this.isLoading = false;
			return;
		}

		this.isLoading = true;
		this.userService.getMyProfile().subscribe({
			next: (userData) => {
				const phone = userData.phone || '';
				const phone2 = userData.phone2 || '';

				this.user = {
					email: userData.email,
					firstName: userData.name,
					lastName: userData.lastName,
					secondLastName: userData.lastName2 || null,
					phone: phone || null,
					phone2: phone2 || null,
					roles: userData.role ? [userData.role.name] : [],
					createdAt: userData.createdAt,
					lastLoginAt: null,
				};
				this.mfa.reset();

				this.profileForm.patchValue({
					firstName: userData.name || '',
					lastName: userData.lastName || '',
					secondLastName: userData.lastName2 || '',
					email: userData.email || '',
					phone: phone,
					phone2: phone2,
				});

				this.showSecondPhone = !!phone2;
				this.mfa.load();
				this.lastLoadTime = Date.now();
				this.isLoading = false;
			},
			error: () => {
				this.isLoading = false;
				this.messageService.showMessage(
					'error',
					this.translocoService.translate('common.labels.error'),
					this.translocoService.translate('profile.messages.errorLoadingProfile'),
				);
			},
		});
	}

	/** Error of a field, shown under it once touched (the form used to show only a generic toast) */
	hasError(field: string): boolean {
		const control = this.profileForm.get(field);
		return !!control?.invalid && !!control?.touched;
	}

	fieldError(field: string): string {
		const errors = this.profileForm.get(field)?.errors;
		if (!errors) return '';
		const key = Object.keys(errors)[0];
		return this.validationService.getValidatorErrorMessage(key, errors[key]);
	}

	onSave(): void {
		if (this.profileForm.invalid) {
			this.profileForm.markAllAsTouched();
			this.messageService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('profile.messages.completeRequiredFields'),
			);
			return;
		}

		const userId = this.currentUserService.getUserId();
		if (!userId) return;

		this.isLoading = true;

		const formValue = this.profileForm.value;

		// A new email needs the current password (the backend checks it)
		const currentPassword: string = formValue.currentPassword || '';
		if (this.emailChanged && !currentPassword) {
			const control = this.profileForm.get('currentPassword');
			control?.setErrors({ required: true });
			control?.markAsTouched();
			this.isLoading = false;
			return;
		}

		const updateData = {
			name: formValue.firstName,
			lastName: formValue.lastName,
			lastName2: formValue.secondLastName || null,
			email: formValue.email,
			phone: formValue.phone || null,
			phone2: formValue.phone2 || null,
			...(this.emailChanged && { currentPassword }),
		};

		this.userService
			.updateOwnProfile(updateData)
			.pipe(
				catchError((err: { error?: { message?: string; code?: ErrorCode } }) => {
					if (
						err?.error?.code === 'INVALID_CURRENT_PASSWORD' ||
						err?.error?.code === 'CURRENT_PASSWORD_REQUIRED'
					) {
						this.profileForm.get('currentPassword')?.setValue('');
					}
					this.messageService.showMessage(
						'error',
						this.translocoService.translate('common.labels.error'),
						// e.g. "that email is already in use" (translated by the HTTP interceptor)
						translateApiError(err, this.translocoService, 'profile.messages.errorUpdatingProfile'),
					);
					return of(null);
				}),
				finalize(() => {
					this.isLoading = false;
				}),
			)
			.subscribe((response) => {
				if (response) {
					this.messageService.showMessage(
						'success',
						this.translocoService.translate('common.labels.success'),
						this.translocoService.translate('profile.messages.profileUpdated'),
					);
					// The sidebar and the top bar read the stored user: refresh its name and email
					const current = this.currentUserService.getCurrentUser();
					if (current) {
						this.currentUserService.setCurrentUser({
							...current,
							displayName:
								`${response.name ?? ''} ${response.lastName ?? ''}`.trim() || current.displayName,
							email: response.email ?? current.email,
						});
					}
					this.isEditMode = false;
					this.originalFormValues = null;
					this.profileForm.get('currentPassword')?.reset('');
					this.invalidateCache();
					AvatarComponent.clearCache(userId);
					this.loadUserProfile();
				}
			});
	}

	enableEditMode(): void {
		this.isEditMode = true;
		// Save original form values
		this.originalFormValues = {
			...this.profileForm.value,
			showSecondPhone: this.showSecondPhone,
		};
	}

	/** The email typed differs from the saved one (then the current password is asked) */
	get emailChanged(): boolean {
		const typed = String(this.profileForm.get('email')?.value ?? '')
			.trim()
			.toLowerCase();
		const saved = String(this.user?.email ?? '')
			.trim()
			.toLowerCase();
		return !!typed && typed !== saved;
	}

	cancelEdit(): void {
		this.isEditMode = false;
		this.profileForm.get('currentPassword')?.reset('');
		// Restore original form values instead of reloading from server
		if (this.originalFormValues) {
			this.profileForm.patchValue({
				firstName: this.originalFormValues.firstName,
				lastName: this.originalFormValues.lastName,
				secondLastName: this.originalFormValues.secondLastName,
				email: this.originalFormValues.email,
				phone: this.originalFormValues.phone,
				phone2: this.originalFormValues.phone2,
			});
			this.showSecondPhone = this.originalFormValues.showSecondPhone;
			this.originalFormValues = null;
		}
	}

	/**
	 * Escape closes the mobile bottom sheets (the desktop p-dialogs handle it themselves).
	 * The top-most sheet closes first; the MFA sheet and confirm dialogs handle their own Escape.
	 */
	@HostListener('document:keydown.escape')
	onEscape(): void {
		if (this.isDesktopView() || this.mfa.showDialog) return;
		if (this.document.querySelector('[role="alertdialog"]')) return;
		if (this.showPasswordDialog) {
			this.onClosePasswordDialog();
		} else if (this.visible) {
			this.onClose();
		}
	}

	onClose(): void {
		this.isEditMode = false;
		this.visibleChange.emit(false);
	}

	/**
	 * Handle dialog hide event to restore z-index
	 */
	onDialogHide(): void {
		this.restoreProfileBarZIndex();
		this.visibleChange.emit(false);
	}

	/**
	 * Lower z-index of profile-bar elements when modal opens
	 */
	private lowerProfileBarZIndex(): void {
		if (!isPlatformBrowser(this.platformId)) return;

		setTimeout(() => {
			const profileBar = this.document.querySelector('.profile-bar');
			const profileMenu = this.document.querySelector('.profile-menu-container');
			const iconBar = this.document.querySelector('.icon-bar');
			const notificationButton = this.document.querySelector('.notification-button');
			const avatarButton = this.document.querySelector('.avatar-button');

			if (profileBar) {
				this.renderer.setStyle(profileBar, 'z-index', '1', 1);
			}
			if (profileMenu) {
				this.renderer.setStyle(profileMenu, 'z-index', '1', 1);
			}
			if (iconBar) {
				this.renderer.setStyle(iconBar, 'z-index', '1', 1);
			}
			if (notificationButton) {
				this.renderer.setStyle(notificationButton, 'z-index', '1', 1);
			}
			if (avatarButton) {
				this.renderer.setStyle(avatarButton, 'z-index', '1', 1);
			}
		}, 0);
	}

	/**
	 * Restore original z-index of profile-bar elements when modal closes
	 */
	private restoreProfileBarZIndex(): void {
		if (!isPlatformBrowser(this.platformId)) return;

		const profileBar = this.document.querySelector('.profile-bar');
		const profileMenu = this.document.querySelector('.profile-menu-container');
		const iconBar = this.document.querySelector('.icon-bar');
		const notificationButton = this.document.querySelector('.notification-button');
		const avatarButton = this.document.querySelector('.avatar-button');

		if (profileBar) {
			this.renderer.setStyle(profileBar, 'z-index', '90');
		}
		if (profileMenu) {
			this.renderer.setStyle(profileMenu, 'z-index', '91');
		}
		if (iconBar) {
			this.renderer.removeStyle(iconBar, 'z-index');
		}
		if (notificationButton) {
			this.renderer.removeStyle(notificationButton, 'z-index');
		}
		if (avatarButton) {
			this.renderer.removeStyle(avatarButton, 'z-index');
		}
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes['visible'] && isPlatformBrowser(this.platformId)) {
			if (this.visible) {
				document.body.style.overflow = 'hidden';
				this.lowerProfileBarZIndex();
				this.isEditMode = false;
				this.loadUserProfile();
			} else {
				document.body.style.overflow = '';
				this.restoreProfileBarZIndex();
				this.isEditMode = false;
			}
		}
	}

	ngOnDestroy() {
		if (isPlatformBrowser(this.platformId)) {
			document.body.style.overflow = '';
		}
	}

	changePassword() {
		this.showPasswordDialog = true;
	}

	/** The inline fields start empty each time: app-change-password is re-created when they open */
	togglePasswordFields(): void {
		this.showPasswordFields = !this.showPasswordFields;
	}

	/** Inline password change cancelled or saved */
	cancelPasswordChange(): void {
		this.showPasswordFields = false;
	}

	onClosePasswordDialog(): void {
		this.showPasswordDialog = false;
	}

	isDesktopView(): boolean {
		return !this.viewportService.isMobileView();
	}

	logout() {
		// Never errors: the session is cleared locally and /login opens even if the backend call fails
		this.authService.logout().subscribe();
	}

	formatMemberSince(dateStr: string | undefined): string {
		if (!dateStr) return '';
		const date = new Date(dateStr);
		return date.toLocaleDateString(activeLocale(this.translocoService), { month: 'long', year: 'numeric' });
	}

	/** Flag of the phone's prefix (Spain/Andorra only) */
	getPhoneFlagUrl(phone: string | null | undefined): string | null {
		return this.phoneService.prefixFlagUrl(phone);
	}

	/** Phone as "+34 612 345 678" (Spain/Andorra; other formats as they are) */
	formatPhoneDisplay(phone: string | null | undefined): string {
		return this.phoneService.formatPrefixed(phone);
	}
}
